import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, RotateCcw, ShieldCheck, Boxes, Beaker, Radio } from 'lucide-react';
import PulseCTA from '../components/PulseCTA';

// Five sharp diagnostic questions — one per critical control area.
// Kept intentionally short so results land at the point of highest engagement.
const questions = [
  { q: 'Do your existing features have end-to-end test coverage validated by business stakeholders?', layer: 'L1' },
  { q: 'Is there a shadow or masked-data environment where agent activity can run safely?', layer: 'L2' },
  { q: 'Are AI-generated actions routed through a risk classification before execution, with two-person review before release?', layer: 'L3' },
  { q: 'Do you record what an agent proposed vs. what it actually did, kept as auditable evidence?', layer: 'L3' },
  { q: 'Do you measure delivery outcomes against baselines with a published evaluation cadence?', layer: 'L4' },
];

const layerMeta = {
  L1: { name: 'Specification & Assurance', icon: <Beaker size={14} />, accent: '#22A94F', halo: 'rgba(34,169,79,0.14)' },
  L2: { name: 'Execution Fabric',          icon: <Boxes size={14} />,  accent: '#5B7BB8', halo: 'rgba(91,123,184,0.16)' },
  L3: { name: 'Governance & Control',      icon: <ShieldCheck size={14} />, accent: '#8B5CF6', halo: 'rgba(139,92,246,0.14)' },
  L4: { name: 'Evidence & Evaluation',     icon: <Radio size={14} />,  accent: '#E6A83A', halo: 'rgba(230,168,58,0.18)' },
};

// Animated arc score dial for the result page.
function ScoreDial({ score, total, label }) {
  const pct = score / total;
  const size = 220;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = pct * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E8E4D9" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="#22A94F"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - dash }}
          transition={{ duration: 1.4, ease: 'easeOut' }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="serif-tight text-[64px] leading-none text-[#1A1A1A]"
        >
          {score}<span className="text-[#1A1A1A]/40 text-[36px]">/{total}</span>
        </motion.div>
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#178838] mt-2">{label}</div>
      </div>
    </div>
  );
}

// Layered signet — the four-layer motif animating alongside progress.
function LayerBadges({ answers }) {
  const perLayer = { L1: 0, L2: 0, L3: 0, L4: 0 };
  const totals = { L1: 0, L2: 0, L3: 0, L4: 0 };
  questions.forEach((q, i) => {
    totals[q.layer] += 1;
    if (answers[i] === 'yes') perLayer[q.layer] += 1;
  });
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Object.entries(layerMeta).map(([id, m], idx) => {
        const filled = perLayer[id];
        const total = totals[id];
        const pct = total ? filled / total : 0;
        return (
          <motion.div
            key={id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * idx }}
            className="rounded-[16px] p-4 border relative overflow-hidden"
            style={{ borderColor: `${m.accent}44`, backgroundColor: `${m.accent}0F` }}
          >
            <div className="flex items-center gap-2" style={{ color: m.accent }}>
              {m.icon}
              <span className="mono text-[10px] tracking-[0.22em] uppercase">{id}</span>
            </div>
            <div className="serif text-[15px] leading-tight mt-2 text-[#1A1A1A]">{m.name}</div>
            <div className="mt-3 h-1 rounded-full bg-[#1A1A1A]/8 overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${pct * 100}%` }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 + 0.1 * idx }}
                style={{ backgroundColor: m.accent }}
              />
            </div>
            <div className="mt-2 mono text-[10px] text-[#1A1A1A]/55">{filled} / {total}</div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function Diagnostic() {
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const currentCount = Object.keys(answers).length;
  const progress = currentCount / questions.length;

  const setAnswer = (i, v) => setAnswers({ ...answers, [i]: v });
  const score = Object.values(answers).filter((v) => v === 'yes').length;

  const { stage, statusLabel, statusTone, statusBody } = useMemo(() => {
    // Five-question scale — thresholds recalibrated proportionally.
    if (score <= 1) return { stage: '01', statusLabel: 'un-gated', statusTone: '#B5822A', statusBody: 'Your estate is not yet ready for AI in production. Fix the ground first — test coverage, environment parity, observability — before adding intelligence.' };
    if (score <= 2) return { stage: '02', statusLabel: 'partially gated', statusTone: '#5B7BB8', statusBody: 'You have the beginnings of safe delivery. Formalise the specification layer and add reviewed release paths before agents write code that ships.' };
    if (score <= 3) return { stage: '03', statusLabel: 'production-ready', statusTone: '#22A94F', statusBody: 'You have the muscle for reviewed agent-assisted delivery. Focus now on the evidence layer — measure outcomes against baselines, publish your evaluation cadence.' };
    return { stage: '04', statusLabel: 'evidence-led', statusTone: '#178838', statusBody: 'You are running the discipline other teams describe. The next stage is intelligent operations — anticipation, not reaction.' };
  }, [score]);

  const blockers = useMemo(() => {
    const gaps = questions.map((q, i) => ({ ...q, answered: answers[i], idx: i })).filter((q) => q.answered !== 'yes');
    return gaps.slice(0, 3).map((g) => g.q);
  }, [answers]);

  if (done) {
    return (
      <div className="bg-[#FBFAF6]">
        {/* Result Hero — vibrant */}
        <section className="relative pt-16 pb-8 overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full"
            style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.16), transparent)' }}
          />
          <div className="dwx relative">
            <div className="relative h-[2px] w-[220px] bg-[#22A94F] mb-8" />
            <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F]">Your result</div>
            <h1 className="serif-tight text-[54px] md:text-[92px] leading-[0.94] mt-4 max-w-4xl">
              You are at Stage <span className="text-[#22A94F]">{stage}</span>,
              <br />
              <span className="text-[#1A1A1A]/55">{statusLabel}.</span>
            </h1>
          </div>
        </section>

        {/* Score + Layer breakdown */}
        <section className="py-12">
          <div className="dwx grid grid-cols-12 gap-10 items-start">
            <div className="col-span-12 md:col-span-4 flex justify-center">
              <ScoreDial score={score} total={questions.length} label="Controls in place" />
            </div>
            <div className="col-span-12 md:col-span-8 space-y-6">
              <p className="text-[17px] leading-relaxed text-[#1A1A1A]/80 border-l-2 pl-4" style={{ borderColor: statusTone }}>
                {statusBody}
              </p>
              <LayerBadges answers={answers} />
            </div>
          </div>
        </section>

        {/* Blockers */}
        {blockers.length > 0 && (
          <section className="py-16 bg-[#F4F2EC] border-y border-[#E8E4D9]">
            <div className="dwx grid grid-cols-12 gap-10 items-start">
              <div className="col-span-12 md:col-span-5">
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#8B5CF6]">Blocking your next stage</div>
                <h2 className="serif-tight text-[38px] md:text-[54px] leading-[0.98] mt-4 text-[#1A1A1A]">
                  Three moves.
                  <br />
                  <span className="text-[#1A1A1A]/45">In order.</span>
                </h2>
              </div>
              <div className="col-span-12 md:col-span-7 space-y-3">
                {blockers.map((b, i) => (
                  <motion.div
                    key={b}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.1 }}
                    className="flex items-start gap-4 rounded-[16px] bg-[#FBFAF6] border border-[#1A1A1A]/10 p-5"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#22A94F]/15 text-[#178838] flex items-center justify-center font-semibold flex-shrink-0">
                      0{i + 1}
                    </div>
                    <div className="text-[15px] leading-relaxed text-[#1A1A1A]/85 pt-1.5">{b}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Primary next-action — surfaced immediately at highest engagement */}
        <section className="py-8">
          <div className="dwx">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="rounded-[24px] bg-[#FBFAF6] border-2 p-8 md:p-10 relative overflow-hidden"
              style={{ borderColor: statusTone }}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full"
                style={{ background: `radial-gradient(closest-side, ${statusTone}22, transparent)` }}
              />
              <div className="relative grid grid-cols-12 gap-6 items-center">
                <div className="col-span-12 md:col-span-8">
                  <div className="mono text-[11px] tracking-[0.24em] uppercase mb-3" style={{ color: statusTone }}>Your natural next step</div>
                  <h2 className="serif-tight text-[32px] md:text-[42px] leading-[1.02] text-[#1A1A1A]">
                    Turn your result into a costed first increment.
                  </h2>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#1A1A1A]/72 max-w-xl">
                    Sixty minutes with two of our engineers. Bring your score, your blockers and one delivery problem. Leave with a written assessment of where AI would help &mdash; and what it would take to find out.
                  </p>
                </div>
                <div className="col-span-12 md:col-span-4 md:flex md:justify-end">
                  <PulseCTA to="/contact" size="lg" variant="green" testId="diagnostic-result-book-session">
                    Book a working session
                  </PulseCTA>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Honest read + restart */}
        <section className="py-16">
          <div className="dwx max-w-4xl">
            <div className="rounded-[20px] bg-[#0F3D22] p-10 text-[#FBFAF6] relative overflow-hidden">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-24 -left-24 w-[400px] h-[400px] rounded-full"
                style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.35), transparent)' }}
              />
              <div className="relative mono text-[11px] tracking-[0.22em] uppercase text-[#7ED4A0]">The honest read</div>
              <p className="relative mt-4 text-[19px] leading-relaxed">
                If your score is 1 or below, the answer is not more AI. Fix the estate first &mdash; test coverage, environment parity, observability. The AI capability arrives when the ground it walks on is stable.
              </p>
              <div className="relative mt-8">
                <button
                  onClick={() => { setAnswers({}); setDone(false); }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#FBFAF6]/25 text-[#FBFAF6] text-[14px] hover:bg-[#FBFAF6]/8 transition-colors"
                  data-testid="diagnostic-restart"
                >
                  <RotateCcw size={14} /> Restart the diagnostic
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-[#FBFAF6]">
      {/* HERO */}
      <section className="relative pt-16 pb-10 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.14), transparent)' }}
        />
        <div className="dwx relative">
          <div className="relative h-[2px] w-[220px] bg-[#22A94F] mb-8" />
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F]">The AI Readiness Diagnostic</div>
          <h1 className="serif-tight text-[54px] md:text-[92px] leading-[0.94] mt-4 max-w-4xl">
            Where are you actually on the
            <br />
            <span className="text-[#22A94F]">AI adoption curve?</span>
          </h1>
          <p className="mt-8 text-[17px] leading-relaxed text-[#1A1A1A]/80 max-w-2xl">
            Five sharp questions across specification, execution, governance and evidence. Two minutes. No call required to see your result.
          </p>
        </div>
      </section>

      {/* PROGRESS + LAYER LEGEND */}
      <section className="pb-10">
        <div className="dwx">
          <div className="rounded-[20px] bg-[#F4F2EC] border border-[#E8E4D9] p-6 md:p-8">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center justify-between mb-3">
                  <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#178838]">Progress</div>
                  <span className="mono text-[11px] text-[#1A1A1A]/60">Question {currentCount} of {questions.length}</span>
                </div>
                <div className="relative h-2 rounded-full bg-[#E8E4D9] overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                      background: 'linear-gradient(90deg, #22A94F 0%, #7ED4A0 100%)',
                    }}
                    animate={{ width: `${progress * 100}%` }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(layerMeta).map(([id, m]) => (
                  <div key={id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] mono uppercase tracking-widest" style={{ color: m.accent, backgroundColor: `${m.accent}12`, borderColor: `${m.accent}40`, borderWidth: 1, borderStyle: 'solid' }}>
                    {m.icon} {id}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUESTIONS */}
      <section className="pb-24">
        <div className="dwx space-y-3">
          {questions.map((q, i) => {
            const m = layerMeta[q.layer];
            const answered = answers[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.35, delay: (i % 6) * 0.03 }}
                className={`group relative rounded-[18px] bg-[#FBFAF6] p-6 md:p-7 border transition-all overflow-hidden ${answered ? 'shadow-[0_4px_20px_-8px_rgba(34,169,79,0.28)]' : 'hover:shadow-[0_4px_20px_-10px_rgba(26,26,26,0.14)]'}`}
                style={{ borderColor: answered ? `${m.accent}55` : 'rgba(26,26,26,0.10)' }}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-16 -left-16 w-[180px] h-[180px] rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                  style={{ background: `radial-gradient(closest-side, ${m.halo}, transparent)` }}
                />
                <div className="relative flex items-start justify-between gap-6 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ color: m.accent, backgroundColor: `${m.accent}12`, borderColor: `${m.accent}40`, borderWidth: 1, borderStyle: 'solid' }}>
                      {m.icon}
                      <span>Q{i + 1} · {q.layer} · {m.name}</span>
                    </div>
                    <div className="serif text-[19px] md:text-[21px] leading-snug mt-3 text-[#1A1A1A]">{q.q}</div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {['yes', 'no', 'unsure'].map((opt) => {
                      const active = answered === opt;
                      const activeBg = opt === 'yes' ? '#178838' : opt === 'no' ? '#A63D2F' : '#5B4FCF';
                      return (
                        <button
                          key={opt}
                          onClick={() => setAnswer(i, opt)}
                          className={`px-4 py-2 rounded-full text-[12px] mono uppercase border transition-all ${active ? 'text-[#FBFAF6] border-transparent' : 'bg-transparent border-[#1A1A1A]/25 text-[#1A1A1A]/70 hover:border-[#1A1A1A]'}`}
                          style={active ? { backgroundColor: activeBg } : {}}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            );
          })}

          <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
            <div className="text-[13px] text-[#1A1A1A]/55">
              {currentCount === questions.length ? 'All answered. Ready when you are.' : `${questions.length - currentCount} to go.`}
            </div>
            <AnimatePresence>
              <motion.button
                key={currentCount === questions.length ? 'ready' : 'wait'}
                initial={{ opacity: 0.6, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: currentCount === questions.length ? 1.02 : 1 }}
                disabled={currentCount < questions.length}
                onClick={() => setDone(true)}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[14px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                style={{
                  backgroundColor: currentCount === questions.length ? '#22A94F' : '#E8E4D9',
                  color: currentCount === questions.length ? '#0F3D22' : '#1A1A1A',
                }}
              >
                See your result <ArrowRight size={16} />
              </motion.button>
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
}
